import { useEffect, useRef, useState } from 'react'
import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react'
import {
  ArrowDown01Icon,
  CloudBigRainIcon,
  CloudFogIcon,
  CloudIcon,
  CloudLightningIcon,
  CloudLittleRainIcon,
  CloudRainIcon,
  CloudSnowIcon,
  FastWindIcon,
  HumidityIcon,
  Location01Icon,
  Moon02Icon,
  MoonCloudIcon,
  Sun03Icon,
  SunCloud01Icon,
  SunCloud02Icon,
  SunriseIcon,
  SunsetIcon,
  TemperatureIcon,
} from '@hugeicons/core-free-icons'

import { useWeather } from '@/hooks/useWeather'
import { WEATHER_PLACE, weatherLabel, type WeatherDay } from '@/lib/weather'
import { cn } from '@/lib/utils'

function weatherIcon(code: number, isDay: boolean): IconSvgElement {
  if (code === 0) return isDay ? Sun03Icon : Moon02Icon
  if (code === 1) return isDay ? SunCloud01Icon : MoonCloudIcon
  if (code === 2) return isDay ? SunCloud02Icon : MoonCloudIcon
  if (code === 3) return CloudIcon
  if (code === 45 || code === 48) return CloudFogIcon
  if (code >= 51 && code <= 57) return CloudLittleRainIcon
  if (code >= 61 && code <= 67) return CloudRainIcon
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return CloudSnowIcon
  if (code >= 80 && code <= 82) return CloudBigRainIcon
  if (code >= 95) return CloudLightningIcon
  return CloudIcon
}

function dayLabel(day: WeatherDay, index: number): string {
  if (index === 0) return 'Today'
  return new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short' })
}

function Metric({
  icon,
  label,
  value,
}: {
  icon: IconSvgElement
  label: string
  value: string
}) {
  return (
    <div className="bg-foreground/5 rounded-control flex flex-col gap-1 px-3 py-2.5">
      <span className="text-foreground/50 flex items-center gap-1.5 text-[11px] font-medium">
        <HugeiconsIcon icon={icon} size={13} strokeWidth={2} />
        {label}
      </span>
      <span className="text-sm font-medium tabular-nums">{value}</span>
    </div>
  )
}

// The header's weather readout: live conditions at the site, with the detail
// hidden behind a dropdown so the chrome stays a single pill.
export function WeatherPill() {
  const { weather, failed } = useWeather()
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // Nothing to expand into until the first reading lands, so the pill stays a
  // plain readout rather than a button that opens an empty panel.
  const ready = weather !== null

  // The panel hangs off the header's pill, not off this button, so its right
  // edge lines up with the chrome rather than with the temperature: the pill
  // carries the theme toggle too and owns the `relative` these coordinates
  // resolve against.
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => ready && setOpen((v) => !v)}
        disabled={!ready}
        aria-expanded={open}
        aria-label={
          ready
            ? `Weather in ${WEATHER_PLACE}, ${Math.round(weather.tempC)} degrees`
            : 'Weather loading'
        }
        className={cn(
          'rounded-control flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium tabular-nums transition-colors',
          ready && 'hover:bg-foreground/5 cursor-pointer',
          open && 'bg-foreground/5',
        )}
      >
        <HugeiconsIcon
          icon={weather ? weatherIcon(weather.code, weather.isDay) : Sun03Icon}
          size={15}
          strokeWidth={2}
        />
        {weather ? `${Math.round(weather.tempC)}°C` : '--°C'}
        <HugeiconsIcon
          icon={ArrowDown01Icon}
          size={13}
          strokeWidth={2}
          className={cn('text-foreground/40 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && weather && (
        <div
          ref={panelRef}
          className="glass dropdown-surface absolute top-full right-0 z-50 mt-2 w-[17.5rem] p-4"
        >
          <div className="text-foreground/50 flex items-center gap-1.5 text-[11px] font-medium">
            <HugeiconsIcon icon={Location01Icon} size={13} strokeWidth={2} />
            {WEATHER_PLACE}
          </div>

          <div className="mt-2.5 flex items-center gap-3">
            <HugeiconsIcon
              icon={weatherIcon(weather.code, weather.isDay)}
              size={34}
              strokeWidth={1.6}
            />
            <div className="flex flex-col">
              <span className="text-2xl leading-none font-semibold tabular-nums">
                {Math.round(weather.tempC)}°C
              </span>
              <span className="text-foreground/60 text-xs">{weatherLabel(weather.code)}</span>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-1.5">
            <Metric
              icon={TemperatureIcon}
              label="Feels like"
              value={`${Math.round(weather.feelsLikeC)}°C`}
            />
            <Metric icon={HumidityIcon} label="Humidity" value={`${Math.round(weather.humidity)}%`} />
            <Metric
              icon={FastWindIcon}
              label="Wind"
              value={`${Math.round(weather.windKph)} km/h`}
            />
            <Metric icon={SunriseIcon} label="Sunrise" value={weather.sunrise} />
          </div>

          <div className="border-foreground/10 mt-3 flex flex-col gap-0.5 border-t pt-3">
            {weather.days.slice(0, 4).map((day, i) => (
              <div key={day.date} className="flex items-center gap-2 py-1 text-xs">
                <span className="text-foreground/60 w-11 shrink-0">{dayLabel(day, i)}</span>
                <HugeiconsIcon
                  icon={weatherIcon(day.code, true)}
                  size={15}
                  strokeWidth={2}
                  className="text-foreground/70 shrink-0"
                />
                <span className="text-foreground/50 flex-1 truncate">{weatherLabel(day.code)}</span>
                <span className="shrink-0 tabular-nums">
                  {Math.round(day.maxC)}°
                  <span className="text-foreground/40"> / {Math.round(day.minC)}°</span>
                </span>
              </div>
            ))}
          </div>

          <div className="text-foreground/40 mt-3 flex items-center justify-between text-[11px]">
            <span className="flex items-center gap-1.5">
              <HugeiconsIcon icon={SunsetIcon} size={12} strokeWidth={2} />
              Sunset {weather.sunset}
            </span>
            <span>{failed ? 'Last known reading' : 'Live · Open-Meteo'}</span>
          </div>
        </div>
      )}
    </>
  )
}
